import { sql } from "drizzle-orm";
import { index, jsonb, pgTable, timestamp, uniqueIndex, varchar, boolean, text } from "drizzle-orm/pg-core";

// Session storage table.
// (IMPORTANT) This table is mandatory for Replit Auth, don't drop it.
export const sessions = pgTable(
  "sessions",
  {
    sid: varchar("sid").primaryKey(),
    sess: jsonb("sess").notNull(),
    expire: timestamp("expire").notNull(),
  },
  (table) => [index("IDX_session_expire").on(table.expire)]
);

// User storage table.
// (IMPORTANT) This table is mandatory for Replit Auth, don't drop it.
//
// `email` stores AES-GCM ciphertext (random IV per write), so its
// uniqueness is enforced via the deterministic `email_hash` column
// instead. See server/lib/encryption.ts.
export const users = pgTable("users", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  email: varchar("email"),
  emailHash: varchar("email_hash", { length: 64 }),
  firstName: varchar("first_name"),
  lastName: varchar("last_name"),
  profileImageUrl: varchar("profile_image_url"),
  emailVerified: boolean("email_verified").notNull().default(false),
  emailVerificationTokenHash: varchar("email_verification_token_hash", { length: 64 }),
  emailVerificationExpiresAt: timestamp("email_verification_expires_at"),
  onboardingCompletedAt: timestamp("onboarding_completed_at"),
  termsAcceptedAt: timestamp("terms_accepted_at"),
  googleId: varchar("google_id", { length: 64 }),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  uniqueIndex("idx_users_email_hash").on(table.emailHash),
  uniqueIndex("idx_users_google_id").on(table.googleId),
]);

export type UpsertUser = typeof users.$inferInsert;
export type User = typeof users.$inferSelect;

// ============================================================================
// STEP-UP SECURITY: Password + TOTP 2FA (per Replit user)
// ============================================================================

export const userSecurity = pgTable("user_security", {
  userId: varchar("user_id").primaryKey().references(() => users.id, { onDelete: "cascade" }),
  passwordHash: text("password_hash"),
  totpSecretEnc: text("totp_secret_enc"),
  mfaEnabled: boolean("mfa_enabled").notNull().default(false),
  mfaEnrolledAt: timestamp("mfa_enrolled_at"),
  // Bcrypt hashes of single-use backup codes. Plaintext is shown to the
  // user exactly once at enrollment / regeneration and never persisted.
  // Stored as a hashed text[] (Task #48) so a database leak cannot turn
  // recovery codes into working sign-in credentials. Successful redemption
  // removes the matching hash from the array (single-use).
  backupCodesHashes: text("backup_codes_hashes").array(),
  passwordResetTokenHash: varchar("password_reset_token_hash", { length: 64 }),
  passwordResetExpiresAt: timestamp("password_reset_expires_at"),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const userSecuritySessions = pgTable("user_security_sessions", {
  id: varchar("id", { length: 36 }).primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  replitSessionId: varchar("replit_session_id"),
  mfaRecentUntil: timestamp("mfa_recent_until"),
  // Human-readable device/browser context shown on the "active sessions" list.
  // Never used for anything security-critical — session identity is `replitSessionId`.
  userAgent: text("user_agent"),
  ipAddress: varchar("ip_address", { length: 64 }),
  createdAt: timestamp("created_at").defaultNow(),
  lastSeenAt: timestamp("last_seen_at").defaultNow(),
  revokedAt: timestamp("revoked_at"),
}, (table) => [
  index("idx_user_security_sessions_user").on(table.userId),
]);

export type UserSecuritySession = typeof userSecuritySessions.$inferSelect;

export type UserSecurity = typeof userSecurity.$inferSelect;
export type InsertUserSecurity = typeof userSecurity.$inferInsert;

